import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios, { setAuthTokenGetter, setAuthenticatedState } from '../services/api';
import log from '../utils/logger';

const useUserSetup = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    setAuthTokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) {
      const handleUser = async () => {
        try {
          const [authType, id] = user.sub.split('|');

          await axios.post('/users', {
            authId: id,
            authType: authType,
            email: user.email,
            userName: user.nickname,
            firstName: user.given_name,
            lastName: user.family_name,
            loggedInTimeStamp: user.updated_at,
            profilePicture: user.picture
          });

          setAuthenticatedState(true);
          setEmailVerified(user.email_verified === true);

          // Check if notifications localStorage is empty and fetch latest 5
          const notificationKey = `notifications_${user.sub}`;
          const storedNotifications = localStorage.getItem(notificationKey);
          
          if (!storedNotifications) {
            try {
              const notificationsResponse = await axios.get('/notifications', {
                params: {
                  channel: 'web',
                  limit: 5
                }
              });
              
              if (notificationsResponse.data.success && notificationsResponse.data.notifications.length > 0) {
                const formattedNotifications = notificationsResponse.data.notifications.map(notification => ({
                  id: notification._id,
                  content: notification.content,
                  origin: notification.origin,
                  contextId: notification.contextId,
                  action: notification.action,
                  isRead: notification.isRead,
                  createdAt: notification.createdAt
                }));
                
                localStorage.setItem(notificationKey, JSON.stringify(formattedNotifications));
                
                // Dispatch custom event to notify Navbar component
                window.dispatchEvent(new CustomEvent('notificationsLoaded', {
                  detail: { notifications: formattedNotifications }
                }));
              }
            } catch (notificationError) {
              log.error('Error fetching notifications:', notificationError);
            }
          }
        } catch (error) {
          log.error('Error handling user:', error.response?.data || error.message);
        } finally {
          setLoading(false);
        }
      };

      handleUser();
    } else {
      setAuthenticatedState(false);
      setEmailVerified(false);
      setLoading(false);
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  return { loading, emailVerified, setEmailVerified };
};

export default useUserSetup;